import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  createBulkSMSAdminService,
  deleteBulkSMSAdminService,
  editBulkSMSAdminService,
  getBulkSMSsAdminService,
  getUsersAdminService,
} from "../../../services/adminServices";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { convertRoleToText } from "../../../utils/enum";

export const bulkSMSAdminContext = createContext({
  bulkSMSs: [],
  setBulkSMSs: () => {},
  bulkSMS: {},
  setBulkSMS: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  type: "",
  setType: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetUsers: () => {},
  handleGetBulkSMSs: () => {},
  handleCreateBulkSMS: () => {},
  handleEditBulkSMS: () => {},
  handleDeleteBulkSMS: () => {},
});

const BulkSMSAdminContext = ({ children }) => {
  const dispatch = useDispatch();

  const [bulkSMSs, setBulkSMSs] = useState([]);
  const [bulkSMS, setBulkSMS] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [type, setType] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(50);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setBulkSMS(val);
      setValue("title", val.title);
      setValue(
        "users",
        val.users?.map((item) => ({
          value: item._id,
          label: `${item.firstName} ${item.lastName} - ${
            item.phoneNumber
          } - ${convertRoleToText(item.role)}`,
        }))
      );
      setValue("message", val.message);
      setValue("type", val.type);
      setValue("description", val.description);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setBulkSMS({});
  };

  // Get Users
  const handleGetUsers = async (filter, callback) => {
    const objData = {
      filter,
      pageNumber: 1,
      itemsCount: 200,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.users?.map((item) => ({
              value: item._id,
              label: `${item.firstName} ${item.lastName} - ${
                item.phoneNumber
              } - ${convertRoleToText(item.role)}`,
            }))
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Bulk SMSs
  const handleGetBulkSMSs = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      filter,
      pageNumber,
      sort,
      itemsCount,
      type,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getBulkSMSsAdminService(objData);
      if (status === 200) {
        setBulkSMSs(data.result?.bulkSMSs);
        setPageCount(data?.result?.allPagesCount);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Bulk SMS
  const handleCreateBulkSMS = async (bulkSMSData) => {
    const objData = {
      ...bulkSMSData,
      users: bulkSMSData?.users?.map((item) => item?.value),
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createBulkSMSAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetBulkSMSs();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Bulk SMS
  const handleEditBulkSMS = async (bulkSMSData) => {
    const objData = {
      id: bulkSMS._id,
      ...bulkSMSData,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editBulkSMSAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetBulkSMSs();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Bulk SMS
  const handleDeleteBulkSMS = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteBulkSMSAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetBulkSMSs();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <bulkSMSAdminContext.Provider
      value={{
        bulkSMSs,
        setBulkSMSs,
        bulkSMS,
        setBulkSMS,
        filter,
        setFilter,
        sort,
        setSort,
        start,
        setStart,
        end,
        setEnd,
        type,
        setType,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetUsers,
        handleGetBulkSMSs,
        handleCreateBulkSMS,
        handleEditBulkSMS,
        handleDeleteBulkSMS,
      }}
    >
      {children}
    </bulkSMSAdminContext.Provider>
  );
};

export default BulkSMSAdminContext;
